/**
 * @greymass/account-creation v0.0.8
 * https://github.com/greymass/account-creation-lib
 *
 * @license
 * Copyright (c) 2021 FFF00 Agents AB & Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { Name, Struct, Serializer, Bytes, isInstanceOf } from '@greymass/eosio';
import { ChainId, Base64u, SigningRequest } from 'eosio-signing-request';
import { __decorate } from 'tslib';

/** Generate a return url that Anchor will redirect back to w/o reload. */
function generateReturnUrl() {
    if (isChromeiOS()) {
        // google chrome on iOS will always open new tab so we just ask it to open again as a workaround
        return 'googlechrome://';
    }
    if (isFirefoxiOS()) {
        // same for firefox
        return 'firefox:://';
    }
    if (isAppleHandheld() && isBrave()) {
        // and brave ios
        return 'brave://';
    }
    if (isAppleHandheld()) {
        // return url with unique fragment required for iOS safari to trigger the return url
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let rv = window.location.href.split('#')[0] + '#';
        for (let i = 0; i < 8; i++) {
            rv += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        }
        return rv;
    }
    if (isAndroid() && isFirefox()) {
        return 'android-intent://org.mozilla.firefox';
    }
    if (isAndroid() && isEdge()) {
        return 'android-intent://com.microsoft.emmx';
    }
    if (isAndroid() && isOpera()) {
        return 'android-intent://com.opera.browser';
    }
    if (isAndroid() && isBrave()) {
        return 'android-intent://com.brave.browser';
    }
    if (isAndroid() && isAndroidWebView()) {
        return 'android-intent://webview';
    }
    if (isAndroid() && isChromeMobile()) {
        return 'android-intent://com.android.chrome';
    }
    return window.location.href;
}
function isAppleHandheld() {
    return /iP(ad|od|hone)/i.test(navigator.userAgent);
}
function isChromeiOS() {
    return /CriOS/.test(navigator.userAgent);
}
function isChromeMobile() {
    return /Chrome\/[.0-9]* Mobile/i.test(navigator.userAgent);
}
function isFirefox() {
    return /Firefox/i.test(navigator.userAgent);
}
function isFirefoxiOS() {
    return /FxiOS/.test(navigator.userAgent);
}
function isOpera() {
    return /OPR/.test(navigator.userAgent) || /Opera/.test(navigator.userAgent);
}
function isEdge() {
    return /Edg/.test(navigator.userAgent);
}
function isBrave() {
    return navigator['brave'] && typeof navigator['brave'].isBrave === 'function';
}
function isAndroid() {
    return /Android/.test(navigator.userAgent);
}
function isAndroidWebView() {
    return /wv/.test(navigator.userAgent) || /Android.*AppleWebKit/.test(navigator.userAgent);
}

class AccountCreator {
    constructor(options) {
        this.options = options;
        this.supportedChains = (options.supportedChains || []).map((id) => ChainId.from(id));
        if (options.scope) {
            this.scope = Name.from(options.scope);
        }
        this.whalesplainerUrl = options.whalesplainerUrl || 'https://create.anchor.link';
        this.returnUrl = options.returnUrl || generateReturnUrl();
    }
    async createAccount() {
        const qs = new URLSearchParams();
        qs.set('return_url', this.returnUrl);
        if (this.supportedChains.length > 0) {
            qs.set('supported_chains', this.supportedChains.map(String).join(','));
        }
        if (this.scope) {
            qs.set('scope', String(this.scope));
        }
        const url = `${this.whalesplainerUrl}/create?${qs}`;
        this.popupWindow = window.open(url, 'targetWindow', `toolbar=no,
            location=no,
            status=no,
            menubar=no,
            scrollbars=yes,
            resizable=yes,
            width=400,
            height=600`);
        return new Promise((resolve, reject) => {
            const listener = (event) => {
                window.removeEventListener('message', listener);
                this.closeDialog();
                if (event.data.error) {
                    reject(new Error(event.data.error));
                }
                else {
                    resolve(event.data);
                }
            };
            window.addEventListener('message', listener);
            this.popupStatusInterval = setInterval(() => {
                if (this.popupWindow && this.popupWindow.closed) {
                    this.closeDialog();
                    reject(new Error('Popup window closed'));
                }
            }, 500);
        });
    }
    closeDialog() {
        this.popupWindow?.close();
        this.cleanup();
    }
    cleanup() {
        this.popupStatusInterval && clearInterval(this.popupStatusInterval);
        this.popupStatusInterval = undefined;
        this.popupWindow = undefined;
    }
}

/**
 * Protocol for the account creation request.
 * `anchorcreate:<base64u_payload>`
 * First byte of payload is version, remainder is ABIencoded CreateRequest.
 */
var CreateRequest_1;
var CreateInfoKey;
(function (CreateInfoKey) {
    CreateInfoKey[CreateInfoKey["login_scope"] = 1] = "login_scope";
    CreateInfoKey[CreateInfoKey["return_path"] = 2] = "return_path";
})(CreateInfoKey || (CreateInfoKey = {}));
let CreateRequestInfo = class CreateRequestInfo extends Struct {
};
__decorate([
    Struct.field('varuint32')
], CreateRequestInfo.prototype, "key", void 0);
__decorate([
    Struct.field('bytes')
], CreateRequestInfo.prototype, "data", void 0);
CreateRequestInfo = __decorate([
    Struct.type('create_request_info')
], CreateRequestInfo);
let CreateRequest = CreateRequest_1 = class CreateRequest extends Struct {
    static from(value) {
        if (typeof value === 'string') {
            return this.fromString(value);
        }
        else {
            const rv = super.from(value);
            if (value.login_scope) {
                rv.login_scope = value.login_scope;
            }
            if (value.return_path) {
                rv.return_path = value.return_path;
            }
            return rv;
        }
    }
    static fromString(value) {
        if (value.slice(0, 13) === 'anchorcreate:') {
            value = value.slice(13);
        }
        if (value.slice(0, 2) === '//') {
            value = value.slice(2);
        }
        const data = Base64u.decode(value);
        const version = data[0];
        if (version !== CreateRequest_1.version) {
            throw new Error(`Unsupported create request version: ${version}`);
        }
        return Serializer.decode({ type: CreateRequest_1, data: data.slice(1) });
    }
    /** Login scope, only valid if login_url is set. */
    get login_scope() {
        return this.getInfo(CreateInfoKey.login_scope, Name) || Name.from('');
    }
    set login_scope(value) {
        this.setInfo(CreateInfoKey.login_scope, Name.from(value));
    }
    /** Return path, where Anchor should redirect once done, use only for same-device requests. */
    get return_path() {
        return this.getInfo(CreateInfoKey.return_path) || '';
    }
    set return_path(value) {
        if (value) {
            this.setInfo(CreateInfoKey.return_path, value);
        }
        else {
            this.removeInfo(CreateInfoKey.return_path);
        }
    }
    /** ESR to sign once account has been created. */
    get loginRequest() {
        if (!this.login_url) {
            return null;
        }
        return SigningRequest.createSync({
            identity: { scope: this.login_scope },
            chainId: null,
            callback: { url: this.login_url, background: true },
            info: {
                return_path: this.return_path,
            },
        });
    }
    encode() {
        const data = Serializer.encode({ object: this });
        return Bytes.from([CreateRequest_1.version]).appending(data);
    }
    toString(includeProtocol) {
        let rv = Base64u.encode(this.encode().array);
        if (includeProtocol !== false) {
            rv = `anchorcreate://${rv}`;
        }
        return rv;
    }
    setInfo(key, value, type) {
        let data;
        if (typeof value === 'string' && !type) {
            data = Bytes.from(value, 'utf8');
        }
        else if (isInstanceOf(value, Bytes) && !type) {
            data = value;
        }
        else {
            data = Serializer.encode({ object: value, type });
        }
        const existing = this.info.find((i) => Number(i.key) === key);
        if (existing) {
            existing.data = data;
        }
        else {
            this.info.push(CreateRequestInfo.from({ key, data }));
        }
    }
    getInfo(key, type) {
        const info = this.info?.find((info) => Number(info.key) === key);
        if (info) {
            if (type) {
                return Serializer.decode({ data: info.data, type });
            }
            else {
                return info.data.utf8String;
            }
        }
    }
    removeInfo(key) {
        this.info = this.info.filter((info) => Number(info.key) !== key);
    }
};
CreateRequest.version = 1;
__decorate([
    Struct.field('string')
], CreateRequest.prototype, "code", void 0);
__decorate([
    Struct.field('string?')
], CreateRequest.prototype, "login_url", void 0);
__decorate([
    Struct.field(CreateRequestInfo, { array: true, extension: true })
], CreateRequest.prototype, "info", void 0);
CreateRequest = CreateRequest_1 = __decorate([
    Struct.type('create_request')
], CreateRequest);

export { AccountCreator, CreateRequest };
//# sourceMappingURL=account-creation.m.js.map
